/* ═══════════════════════════════════════════════════════════════
   uiScale — the ONE curve that turns window width + a user preference
   into a root font-size multiplier (DESIGN.md §3.6).

   Root font-size = 16px × dial × user. The dial auto-scales the whole
   app up on very large monitors (1.00 at ≤1920 CSS px window width,
   linear to 1.20 at ≥2560) so nothing looks tiny on a 4K display; the
   user factor is a manual +/-5% step the user can layer on top,
   persisted per device. Nothing else in the app sizes by window width —
   nothing but `autoScale` reads `window.innerWidth`, so there is exactly
   one place that curve can ever be wrong.

   Pure TypeScript: no Node, no DOM. The browser controller that reads
   `window.innerWidth` / localStorage / writes CSS lives in
   `src/renderer/lib/uiScale.ts` and calls straight through to this file.
   ═══════════════════════════════════════════════════════════════ */

/** CSS px window width at/below which the dial is at its minimum (1.00). */
export const DIAL_MIN_WIDTH = 1920;
/** CSS px window width at/above which the dial is at its maximum (1.20). */
export const DIAL_MAX_WIDTH = 2560;
/** Dial value at DIAL_MIN_WIDTH and below. */
export const DIAL_MIN = 1.0;
/** Dial value at DIAL_MAX_WIDTH and above. */
export const DIAL_MAX = 1.2;
/** One Ctrl+=/Ctrl+- keyboard step, as a fraction of the user factor. */
export const USER_STEP = 0.05;
/** Effective (dial × user) scale never renders below this. */
export const SCALE_MIN = 0.75;
/** Effective (dial × user) scale never renders above this. */
export const SCALE_MAX = 1.75;
/** The base root font-size the whole app's rem values are relative to. */
export const ROOT_PX = 16;
/** Project dashboard collapses to one column below this container width (rem × live root px). */
export const DASHBOARD_SINGLE_COLUMN_REM = 56;

/** The three numbers that describe "how big is the app right now." */
export interface UiScaleValues {
  /** Auto value from window width, DIAL_MIN..DIAL_MAX. */
  dial: number;
  /** Manual user preference, persisted per device. */
  user: number;
  /** dial × user, clamped to [SCALE_MIN, SCALE_MAX] — what actually renders. */
  effective: number;
}

/** Round to 2 decimal places (0.01 steps) — every scale value in this
 *  module is quantized here so comparisons/persistence are exact. */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

/** Window-width → dial. Linear between DIAL_MIN_WIDTH/DIAL_MAX_WIDTH,
 *  flat outside that range, rounded to 0.01. The ONLY function in the
 *  app allowed to read a window-width curve — everything else that wants
 *  "how big should things be" reads `effectiveScale`/`dial` instead. */
export function autoScale(widthCssPx: number): number {
  if (widthCssPx <= DIAL_MIN_WIDTH) return DIAL_MIN;
  if (widthCssPx >= DIAL_MAX_WIDTH) return DIAL_MAX;
  const t = (widthCssPx - DIAL_MIN_WIDTH) / (DIAL_MAX_WIDTH - DIAL_MIN_WIDTH);
  return round2(DIAL_MIN + t * (DIAL_MAX - DIAL_MIN));
}

/** dial × user, clamped to [SCALE_MIN, SCALE_MAX] and rounded to 0.01 —
 *  the number that actually multiplies ROOT_PX. */
export function effectiveScale(dial: number, user: number): number {
  return round2(clamp(dial * user, SCALE_MIN, SCALE_MAX));
}

/** The user factor's own legal range. Both bounds are taken against the
 *  SMALLEST dial (1.00), which is the dial almost every window sits at:
 *  a user factor must be able to reach the full 0.75-1.75 effective range
 *  there, otherwise a Settings option like "150%" could never be selected.
 *  `effectiveScale` is the real backstop — at a larger dial it clamps the
 *  product back to SCALE_MAX, so widening this range can never render past
 *  the law's limits. */
export function clampUser(user: number): number {
  const min = SCALE_MIN / DIAL_MIN;
  const max = SCALE_MAX / DIAL_MIN;
  return round2(clamp(user, min, max));
}

/** One Ctrl+=/Ctrl+- keyboard step, clamped and rounded. */
export function stepUser(user: number, dir: 1 | -1): number {
  return clampUser(round2(user + dir * USER_STEP));
}

/** "119%" — for the Settings row and the change toast. */
export function formatPercent(scale: number): string {
  return Math.round(scale * 100) + '%';
}
